/**
 * Bridge between the wire shape (`ServerAnnotation`) and the local
 * `Measurement` + Three.js geometry produced by each tool.
 *
 * Used by Scene.tsx on initial mount (restore from REST) and on every
 * WS `annotation:create` event from another user.
 */

import { Sprite, SpriteMaterial, Vector3 } from 'three'

import type {
  AnnotationKind,
  ServerAnnotation,
  CreateAnnotationPayload,
} from '../api/annotationsClient'
import type { Measurement } from './measurements'
import type { AreaTool } from './tools/AreaTool'
import { AuthorChip, makePixelLabel } from './tools/DistanceTool'
import { makeWrappedLabel, NoteTool } from './tools/NoteTool'
import type { DistanceTool } from './tools/DistanceTool'

export interface Tools {
  distance: DistanceTool
  area: AreaTool
  note: NoteTool
}

function vec3FromPoint(p: { x: number; y: number; z: number }): Vector3 {
  return new Vector3(p.x, p.y, p.z)
}

/** Pull the serial out of a label like "Linie 7 — 12.34 m". Returns 0 if
 * the label doesn't match the expected pattern (e.g. user renamed it). */
function parseSerial(label: string): number {
  const m = /^(?:Linie|Fläche|Notiz)\s+(\d+)/.exec(label)
  return m ? parseInt(m[1], 10) : 0
}

/** Build a `Measurement` (Three.js group + state row) from a server payload. */
export function measurementFromServer(
  ann: ServerAnnotation,
  tools: Tools,
): Measurement {
  const build = () => {
    if (ann.kind === 'distance') {
      const points = ann.points.map(vec3FromPoint)
      return tools.distance.buildGroupFromPayload(points, ann.color).group
    }
    if (ann.kind === 'area') {
      const verts = ann.points.map(vec3FromPoint)
      return tools.area.buildGroupFromPayload(verts, ann.color).group
    }
    const anchor = vec3FromPoint(ann.points[0])
    return tools.note.buildGroupFromPayload(anchor, ann.color, ann.text || ann.label).group
  }
  const group = build()
  group.name = `${ann.kind}-${ann.id}`
  // Stamp the id so a raycast hit on any descendant resolves back to the
  // measurement (used by the hover tooltip).
  group.userData.measurementId = ann.id
  return {
    id: ann.id,
    kind: ann.kind,
    label: ann.label,
    visible: true,
    group,
    color: ann.color,
    createdBy: ann.created_by,
    createdAt: ann.created_at,
  }
}

/** Re-create geometry for a list of saved annotations and advance each
 * tool's per-session serial past the highest restored value. */
export function restoreAnnotations(
  items: ServerAnnotation[],
  tools: Tools,
): Measurement[] {
  let maxLine = 0
  let maxFlach = 0
  let maxNotiz = 0
  const out: Measurement[] = []
  for (const ann of items) {
    try {
      out.push(measurementFromServer(ann, tools))
    } catch (err) {
      console.warn('[Annotations] restore failed for', ann.id, err)
      continue
    }
    const n = parseSerial(ann.label)
    if (ann.kind === 'distance' && n > maxLine) maxLine = n
    if (ann.kind === 'area' && n > maxFlach) maxFlach = n
    if (ann.kind === 'note' && n > maxNotiz) maxNotiz = n
  }
  tools.distance.setSerial(maxLine)
  tools.area.setSerial(maxFlach)
  tools.note.setSerial(maxNotiz)
  return out
}

/** Convert a local Measurement into the POST payload. The `text` field is
 * derived from the label suffix for notes ("Notiz N — <body>"). */
export function toCreatePayload(m: Measurement): CreateAnnotationPayload {
  const points = collectPoints(m, m.kind)
  let text = ''
  if (m.kind === 'note') {
    const sep = m.label.indexOf('—')
    text = sep >= 0 ? m.label.slice(sep + 1).trim() : m.label
  }
  return {
    id: m.id,
    kind: m.kind,
    points,
    color: m.color,
    label: m.label,
    text,
  }
}

/** Pull the world-space vertex list back out of the rendered group.
 *
 * Tools don't store the source points anywhere after `finish()`, so we
 * read them off the cone markers (one cone per vertex, in click order).
 * `userData.annotationVertex` is stamped by `buildMarkerCone` callers —
 * but for live-created groups we fall back to reading positions out of
 * any `Mesh` whose name starts with "marker-cone" (set by markerCone.ts).
 */
function collectPoints(m: Measurement, kind: AnnotationKind): { x: number; y: number; z: number }[] {
  const points: { x: number; y: number; z: number }[] = []
  m.group.traverse((node) => {
    if (node.userData?.isAnnotationVertex) {
      points.push({ x: node.position.x, y: node.position.y, z: node.position.z })
    }
  })
  if (points.length === 0) {
    console.warn('[Annotations] no vertices recoverable from group; tools must stamp markers', kind)
  }
  return points
}

/** Deterministic color (0xRRGGBB) for a user id — used for cursors + badges.
 * Same id always yields the same hue; spread across 12 well-separated hues. */
export function colorForUser(userId: string): number {
  let h = 0
  for (let i = 0; i < userId.length; i++) {
    h = (h * 31 + userId.charCodeAt(i)) >>> 0
  }
  const palette = [
    0xe53935, 0xd81b60, 0x8e24aa, 0x5e35b1,
    0x3949ab, 0x1e88e5, 0x039be5, 0x00897b,
    0x43a047, 0xc0ca33, 0xfb8c00, 0xf4511e,
  ]
  return palette[h % palette.length]
}

/** "Max Mustermann" → "MM"; falls back to email's first 2 chars. */
export function initialsFor(author: { first_name?: string; last_name?: string; email?: string } | null | undefined): string {
  if (!author) return '?'
  const f = (author.first_name ?? '').trim()
  const l = (author.last_name ?? '').trim()
  if (f || l) {
    return ((f[0] ?? '') + (l[0] ?? '')).toUpperCase() || '?'
  }
  const e = (author.email ?? '').trim()
  if (e) return e.slice(0, 2).toUpperCase()
  return '?'
}

/** Build the AuthorChip spec for the painter from a server-provided author. */
function authorChipFor(m: Measurement): AuthorChip | null {
  const author = m.createdBy
  if (!author) return null
  return {
    initials: initialsFor(author),
    color: colorForUser(author.id),
  }
}

interface PixelLabelSpec {
  kind: 'pixel'
  text: string
  bg: number
  fg: number
  bold: boolean
  fontSizeCss: number
}

interface WrappedLabelSpec {
  kind: 'wrapped'
  text: string
  bg: number
  fg: number
}

type LabelSpec = PixelLabelSpec | WrappedLabelSpec

/** Rebuild a measurement-value or note label sprite in place, swapping in
 * (or out) the author chip. Returns the replacement sprite or `null` if
 * the sprite is missing the metadata required to rebuild it (e.g. legacy
 * sprite created before this code shipped). */
function rebuildLabel(sprite: Sprite, chip: AuthorChip | null): Sprite | null {
  const spec = sprite.userData.labelSpec as LabelSpec | undefined
  const anchor = sprite.userData.labelAnchor as Vector3 | undefined
  const parent = sprite.parent
  if (!spec || !anchor || !parent) return null

  // Preserve the user's "Maßtexte" / "Textgröße" slider multiplier so the
  // rebuilt sprite keeps the visual size the previous one had.
  const base = sprite.userData as { baseScaleX?: number; baseScaleY?: number }
  const appliedScale =
    typeof base.baseScaleX === 'number' && base.baseScaleX > 0
      ? sprite.scale.x / base.baseScaleX
      : 1

  const flags = {
    isMeasureLabel: sprite.userData.isMeasureLabel === true,
    isNoteLabel: sprite.userData.isNoteLabel === true,
  }
  parent.remove(sprite)
  const mat = sprite.material as SpriteMaterial
  mat.map?.dispose()
  mat.dispose()

  const replacement =
    spec.kind === 'pixel'
      ? makePixelLabel(spec.text, anchor, spec.bg, spec.fg, spec.bold, spec.fontSizeCss, chip)
      : makeWrappedLabel(spec.text, anchor, spec.bg, spec.fg, chip)
  if (flags.isMeasureLabel) replacement.userData.isMeasureLabel = true
  if (flags.isNoteLabel) replacement.userData.isNoteLabel = true
  replacement.scale.multiplyScalar(appliedScale)
  parent.add(replacement)
  return replacement
}

/** Re-render every measurement label (value `X.XX m`, area `Y.YY m²`,
 * note text) inside a measurement's group with (or without) the author
 * chip baked into the top-right corner. */
export function applyAuthorChipsToMeasurement(m: Measurement, show: boolean): void {
  const chip = show ? authorChipFor(m) : null
  // Snapshot first — rebuildLabel swaps the sprite for a new one; mutating
  // a parent's children during traversal would skip siblings.
  const labels: Sprite[] = []
  m.group.traverse((node) => {
    const ud = node.userData
    if ((node as Sprite).isSprite && (ud?.isMeasureLabel || ud?.isNoteLabel)) {
      labels.push(node as Sprite)
    }
  })
  for (const sprite of labels) rebuildLabel(sprite, chip)
}

/** Apply the toggle across a whole measurement list. Called by the Toolbar
 * checkbox + after any measurement mutation that might add new labels. */
export function setAuthorChipsVisible(items: Measurement[], visible: boolean): void {
  for (const m of items) applyAuthorChipsToMeasurement(m, visible)
}

// Kept as a back-compat alias so the Scene's onAdd handler reads naturally.
export const ensureAuthorChip = applyAuthorChipsToMeasurement

// Silence "unused import" warning — NoteTool is referenced via the
// `Tools` interface above (typeof import is structural).
void NoteTool
